// Extract the first viable frame of a video to a JPEG Blob, entirely in-browser.
// Works on iOS Safari 17+, Android Chrome, desktop Chrome/Safari/Firefox.
// Returns null if the browser can't decode the format (HEIC video, AV1, etc.).
export async function extractVideoPoster(file, { maxSide = 400, seekTo = 0.5 } = {}) {
  return new Promise((resolve) => {
    let url = null;
    let video = null;
    const cleanup = () => {
      try { if (url) URL.revokeObjectURL(url); } catch (_) { /* noop */ }
      try { if (video) video.remove(); } catch (_) { /* noop */ }
    };
    const fail = () => { cleanup(); resolve(null); };

    try {
      url = URL.createObjectURL(file);
      video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.playsInline = true;
      video.src = url;
      video.crossOrigin = "anonymous";

      const draw = () => {
        try {
          const vw = video.videoWidth;
          const vh = video.videoHeight;
          if (!vw || !vh) return fail();
          const side = Math.min(vw, vh);
          const sx = (vw - side) / 2;
          const sy = (vh - side) / 2;
          const canvas = document.createElement("canvas");
          canvas.width = maxSide;
          canvas.height = maxSide;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, sx, sy, side, side, 0, 0, maxSide, maxSide);
          canvas.toBlob((blob) => {
            cleanup();
            resolve(blob || null);
          }, "image/jpeg", 0.82);
        } catch (_) { fail(); }
      };

      const onSeeked = () => {
        video.removeEventListener("seeked", onSeeked);
        // Give the browser one frame to paint
        requestAnimationFrame(draw);
      };
      const onLoaded = () => {
        video.removeEventListener("loadedmetadata", onLoaded);
        const target = Math.min(seekTo, Math.max(0, (video.duration || 1) * 0.1));
        video.addEventListener("seeked", onSeeked);
        try { video.currentTime = target; } catch (_) { fail(); }
      };
      video.addEventListener("loadedmetadata", onLoaded);
      video.addEventListener("error", fail);

      // 8s safety timeout — some iPhone videos refuse to decode in the background tab
      setTimeout(fail, 8000);
    } catch (_) { fail(); }
  });
}

// Post a generated poster JPEG to the backend so the gallery shows a real frame instead of a placeholder.
// Use the shared axios client so the X-Guest-Id header (set by an interceptor) is attached —
// without it the backend rejects with 403 because the uploader can't be verified.
import client from "./api";

export async function uploadMediaThumb(mediaId, blob) {
  const form = new FormData();
  form.append("thumb", blob, "poster.jpg");
  try {
    await client.post(`/media/${mediaId}/thumb`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60_000,
    });
    return true;
  } catch (err) {
    // Non-blocking — uploads themselves still succeeded.
    if (process.env.NODE_ENV !== "production") {
      console.warn("Thumb upload failed for", mediaId, err?.response?.status, err?.response?.data);
    }
    return false;
  }
}
